/**
 * Who may do what. The API enforces these on every write; the web app uses the same
 * functions to hide controls, so the two can never disagree.
 */
export const ROLES = [
  'owner',
  'org_admin',
  'quality_manager',
  'site_admin',
  'pathologist',
  'technician',
  'accession',
  'front_desk',
] as const;
export type Role = (typeof ROLES)[number];

export const ROLE_LABELS: Record<Role, string> = {
  owner: 'Owner',
  org_admin: 'Organization admin',
  quality_manager: 'Quality manager',
  site_admin: 'Lab admin',
  pathologist: 'Pathologist',
  technician: 'Technician',
  accession: 'Accession',
  front_desk: 'Front desk',
};

export function isRole(x: string): x is Role {
  return (ROLES as readonly string[]).includes(x);
}

const has = (roles: readonly string[], ...allowed: Role[]) =>
  roles.some((r) => (allowed as string[]).includes(r));

/** Tables the server writes itself; clients may never push them. */
export const SERVER_OWNED_TABLES = ['members', 'devices', 'number_blocks'] as const;

const ADMIN: Role[] = ['owner', 'org_admin', 'site_admin'];
const TEMPLATE_EDITORS: Role[] = ['owner', 'quality_manager', 'site_admin', 'pathologist'];
const FLOOR: Role[] = [
  'owner',
  'site_admin',
  'pathologist',
  'technician',
  'accession',
  'front_desk',
];

/** Who handles prices and payments at the counter. Private to the lab: never printed on a report. */
const BILLING: Role[] = ['owner', 'org_admin', 'site_admin', 'accession', 'front_desk'];

/** May these roles push changes to this synced table? */
export function canWriteTable(roles: readonly string[], table: string): boolean {
  switch (table) {
    case 'sites':
    case 'brand_settings':
      return has(roles, ...ADMIN);
    case 'signatories':
      return has(roles, ...ADMIN, 'quality_manager');
    case 'templates':
    case 'template_versions':
      return has(roles, ...TEMPLATE_EDITORS);
    case 'patients':
    case 'visits':
    case 'reports':
    case 'folders':
      return has(roles, ...FLOOR);
    case 'sample_types':
      return has(roles, ...FLOOR);
    case 'payments':
    case 'expenses':
      return has(roles, ...BILLING);
    case 'report_versions':
      return has(roles, 'owner', 'pathologist');
    case 'files':
    case 'audit_events':
      return has(roles, ...FLOOR, 'org_admin', 'quality_manager');
    default:
      return false;
  }
}

export type Action =
  | 'manage_org'
  | 'manage_site'
  | 'invite_users'
  | 'edit_templates'
  | 'register_visit'
  | 'enter_results'
  | 'verify_results'
  | 'sign_report'
  | 'amend_report'
  | 'revoke_report'
  | 'receive_samples'
  | 'send_report'
  | 'view_dashboard'
  /** See prices and payments. */
  | 'view_billing'
  /** Record payments and change a report's discount. */
  | 'manage_billing'
  /** See the lab's income and outstanding amounts on the dashboard. */
  | 'view_finance'
  /** See and record the lab's expenses and their bill photos. */
  | 'manage_expenses'
  /** Delete a signed report, or a patient who has signed reports. Unsigned work can be deleted by the whole floor. */
  | 'delete_signed';

export function can(roles: readonly string[], action: Action): boolean {
  switch (action) {
    case 'manage_org':
      return has(roles, 'owner', 'org_admin');
    case 'manage_site':
    case 'invite_users':
      return has(roles, ...ADMIN);
    case 'edit_templates':
      return has(roles, ...TEMPLATE_EDITORS);
    case 'register_visit':
      return has(roles, ...FLOOR);
    case 'enter_results':
      return has(roles, 'owner', 'pathologist', 'technician');
    case 'verify_results':
      return has(roles, 'owner', 'pathologist', 'technician');
    case 'sign_report':
    case 'amend_report':
    case 'revoke_report':
      return has(roles, 'owner', 'pathologist');
    case 'receive_samples':
      return has(roles, 'owner', 'site_admin', 'technician', 'accession');
    case 'send_report':
      return has(roles, ...FLOOR);
    case 'view_dashboard':
      return has(roles, 'owner', 'org_admin', 'quality_manager', 'site_admin', 'pathologist');
    case 'view_billing':
    case 'manage_billing':
    case 'manage_expenses':
      return has(roles, ...BILLING);
    case 'view_finance':
      return has(roles, 'owner', 'org_admin', 'site_admin');
    case 'delete_signed':
      return has(roles, 'owner', 'site_admin');
  }
}

export const REPORT_STATUSES = [
  'draft',
  'awaiting_verification',
  'awaiting_signature',
  'partial',
  'final',
  'amended',
  'revoked',
] as const;
export type ReportStatus = (typeof REPORT_STATUSES)[number];

export type WorkflowLevel = 'simple' | 'standard' | 'nabl';

/** Statuses in which the working copy may still be edited. */
export const EDITABLE_STATUSES: readonly ReportStatus[] = [
  'draft',
  'awaiting_verification',
  'awaiting_signature',
  'amended',
];

export const isEditable = (s: ReportStatus) => EDITABLE_STATUSES.includes(s);
export const isFinalLike = (s: ReportStatus) => s === 'final' || s === 'partial' || s === 'revoked';

/**
 * The next statuses a person with these roles may move a report to, given the lab's
 * workflow level. Simple labs sign straight from draft; NABL labs need a verification step.
 */
export function nextStatuses(
  level: WorkflowLevel,
  status: ReportStatus,
  roles: readonly string[],
): ReportStatus[] {
  const out: ReportStatus[] = [];
  const canEnter = can(roles, 'enter_results');
  const canSign = can(roles, 'sign_report');
  switch (status) {
    case 'draft':
    case 'amended':
      if (level === 'simple') {
        if (canSign) out.push('final', 'partial');
      } else if (level === 'standard') {
        if (canEnter) out.push('awaiting_signature');
        if (canSign) out.push('final', 'partial');
      } else {
        if (canEnter) out.push('awaiting_verification');
      }
      break;
    case 'awaiting_verification':
      if (can(roles, 'verify_results')) out.push('awaiting_signature');
      if (canEnter) out.push(status === 'awaiting_verification' ? 'draft' : status);
      break;
    case 'awaiting_signature':
      if (canSign) out.push('final', 'partial');
      if (canEnter) out.push('draft');
      break;
    case 'partial':
      if (canSign) out.push('final');
      break;
    case 'final':
      if (can(roles, 'amend_report')) out.push('amended');
      if (can(roles, 'revoke_report')) out.push('revoked');
      break;
    case 'revoked':
      break;
  }
  return [...new Set(out)];
}

/** The roles a person may hand to others. Nobody can grant more than they hold. */
export function grantableRoles(granterRoles: readonly string[]): Role[] {
  const out = new Set<Role>();
  if (granterRoles.includes('owner')) for (const r of ROLES) out.add(r);
  if (granterRoles.includes('org_admin')) for (const r of ROLES) if (r !== 'owner') out.add(r);
  if (granterRoles.includes('site_admin')) {
    for (const r of ['pathologist', 'technician', 'accession', 'front_desk'] as Role[]) out.add(r);
  }
  return ROLES.filter((r) => out.has(r));
}

/**
 * Can these roles take a report from one status to another, possibly through several steps?
 * A device that was offline records the steps together; the server accepts the result only if
 * the same person could have made each step themselves.
 */
export function canReach(
  level: WorkflowLevel,
  from: ReportStatus,
  to: ReportStatus,
  roles: readonly string[],
): boolean {
  if (from === to) return true;
  const seen = new Set<ReportStatus>([from]);
  const queue: ReportStatus[] = [from];
  while (queue.length) {
    const cur = queue.shift() as ReportStatus;
    for (const next of nextStatuses(level, cur, roles)) {
      if (next === to) return true;
      if (!seen.has(next)) {
        seen.add(next);
        queue.push(next);
      }
    }
  }
  return false;
}
